import React from 'react';
import { ArrowRight } from 'lucide-react';
import { cn } from '../../lib/utils';
import Iridescence from '../../components/Iridescence';
import { usePriorityAccessModal } from '../../contexts/priority-access-modal-context';
import Analytics from '../../lib/analytics';

interface HeroSectionProps {
  className?: string;
}

const HeroSectionComponent: React.FC<HeroSectionProps> = ({ className }) => {
  const { openModal } = usePriorityAccessModal();

  const handleCtaClick = () => {
    Analytics.trackEvent('hero_cta_click', {
      event_category: 'Hero',
      event_label: 'Get Priority Access',
      trigger_location: 'hero',
    });
    openModal();
  };
  return (
    <section
      id="hero"
      className={cn(
        'relative flex min-h-screen items-center justify-center overflow-hidden',
        'max-[599px]:min-h-screen max-[599px]:items-center',
        className
      )}
    >
      {/* Content container */}
      <div className="relative z-10 container mx-auto px-6 pt-24 max-[599px]:pt-[8vh] max-[599px]:pb-[6vh] sm:px-8 lg:px-12">
        <div className="mx-auto max-w-5xl text-center">
          {/* Top bubble/pill - subtle with animated border */}
          <div className="mb-6 inline-flex items-center justify-center max-[599px]:mb-[3vh]">
            <div className="animate-border-beam relative rounded-full">
              <div className="inline-flex items-center gap-2 rounded-full bg-gray-100/30 px-4 py-2 backdrop-blur-lg max-[599px]:px-2.5 max-[599px]:py-1">
                <div className="relative flex h-2 w-2 max-[599px]:h-1.5 max-[599px]:w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-pulse rounded-full bg-purple-400 opacity-75"></span>
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-purple-500 max-[599px]:h-1.5 max-[599px]:w-1.5"></span>
                </div>
                <span className="text-[10px] font-normal tracking-wider text-gray-500 uppercase max-[599px]:text-[7px]">
                  Business Assistant for SMB leaders
                </span>
              </div>
            </div>
          </div>

          {/* Main heading with appropriate font sizes */}
          <h1 className="font-primary mb-8 max-[599px]:mb-[4vh]">
            <span className="block text-5xl leading-tight font-semibold tracking-tight text-slate-900 max-[599px]:text-[10vw] max-[599px]:leading-[1.05] max-[379px]:text-[11vw] max-[319px]:text-[12vw] sm:text-6xl lg:text-7xl">
              Discover{' '}
              <span className="relative inline-block">
                <span className="relative inline-flex items-center gap-2 rounded-2xl border border-white/20 bg-white/10 px-5 py-2 shadow-[0_8px_32px_rgba(31,_38,_135,_0.07),inset_0_2px_0_0_rgba(255,255,255,0.15),inset_0_-2px_0_0_rgba(0,0,0,0.05)] backdrop-blur-md backdrop-saturate-150 max-[599px]:rounded-xl max-[599px]:px-3 max-[599px]:py-1 max-[379px]:px-2 max-[379px]:py-0.5 max-[319px]:px-1.5 max-[319px]:py-0.5">
                  <span
                    className="relative font-bold"
                    style={{ color: '#1E3A8A' }}
                  >
                    growth opportunities
                  </span>
                </span>
              </span>
            </span>
            <span className="mt-4 block text-5xl leading-tight font-semibold tracking-tight text-slate-900 max-[599px]:mt-[0.5vh] max-[599px]:text-[10vw] max-[599px]:leading-[1.05] max-[379px]:text-[11vw] max-[319px]:text-[12vw] sm:text-6xl lg:text-7xl">
              and{' '}
              <span className="relative inline-block">
                <span className="relative inline-flex items-center gap-2 rounded-2xl border border-white/20 bg-white/10 px-5 py-2 shadow-[0_8px_32px_rgba(31,_38,_135,_0.07),inset_0_2px_0_0_rgba(255,255,255,0.15),inset_0_-2px_0_0_rgba(0,0,0,0.05)] backdrop-blur-md backdrop-saturate-150 max-[599px]:rounded-xl max-[599px]:px-3 max-[599px]:py-1 max-[379px]:px-2 max-[379px]:py-0.5 max-[319px]:px-1.5 max-[319px]:py-0.5">
                  <span
                    className="relative font-bold"
                    style={{ color: '#1E3A8A' }}
                  >
                    profit leaks
                  </span>
                </span>
              </span>{' '}
              instantly
            </span>
          </h1>

          {/* Subheading - smaller and more professional */}
          <p className="mx-auto mb-10 max-w-4xl text-lg leading-relaxed font-normal text-slate-600 max-[599px]:mb-[3vh] max-[599px]:text-sm sm:text-xl">
            Unlock smarter, data-driven decisions with AI insights from your
            ERP, CRM, IMS & finance tools.
          </p>

          {/* Primary CTA Button */}
          <div className="flex items-center justify-center">
            <button
              onClick={handleCtaClick}
              className={cn(
                'inline-flex items-center gap-2',
                'px-8 py-4 max-[599px]:px-6 max-[599px]:py-3',
                'bg-[rgb(59,91,235)] text-white',
                'rounded-lg',
                'text-lg font-semibold max-[599px]:text-sm',
                'transition-all duration-200 ease-in-out',
                'hover:scale-[1.03] hover:bg-[rgb(79,111,255)]',
                'focus:ring-2 focus:ring-[rgb(59,91,235)]/50 focus:ring-offset-2 focus:outline-none',
                'shadow-lg hover:shadow-xl'
              )}
            >
              Get Priority Access
              <ArrowRight className="h-5 w-5 max-[599px]:h-4 max-[599px]:w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Iridescence Background */}
      <div className="absolute inset-0 -z-10">
        <Iridescence
          color={[0.92, 0.94, 0.96]} // Very light blue-gray tones
          speed={0.12} // Even slower for minimal distraction
          amplitude={0.03} // Very gentle mouse interaction
          mouseReact={true}
        />
      </div>
    </section>
  );
};

// Memoize the component to prevent unnecessary re-renders
export const HeroSection = React.memo(HeroSectionComponent);
